import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { DebuggerOptionName, DebuggerOptionNames, DebuggerOptions } from '../debugger.model';
import { UrlParamsService } from './url-params.service';

type DebuggerOptionsObservables = {
  [key in keyof DebuggerOptions]: Observable<DebuggerOptions[key]>
};

@Injectable({ providedIn: 'root' })
export class OptionsService {
  private readonly url: UrlParamsService;
  private readonly options$: BehaviorSubject<DebuggerOptions>;
  private readonly observables$: DebuggerOptionsObservables;
  private readonly all$: Observable<DebuggerOptions>;

  public constructor() {
    this.url = inject(UrlParamsService);
    this.options$ = new BehaviorSubject<DebuggerOptions>(this.url.readOptions());
    this.observables$ = this.createObservables();
    this.all$ = combineLatest(this.observables$);
  }

  public get(name: DebuggerOptionName): Observable<string> {
    return this.observables$[name];
  }

  public all(): Observable<DebuggerOptions> {
    return this.all$;
  }

  public update(options: Partial<DebuggerOptions>): void {
    this.options$.next({ ...this.options$.getValue(), ...options });
    this.url.updateOptions(this.options$.getValue());
  }

  private createObservables(): DebuggerOptionsObservables {
    return DebuggerOptionNames.reduce((observables, name) => {
      observables[name] = this.options$.pipe(
        map(option => option[name]),
        distinctUntilChanged(),
      );

      return observables;
    }, {} as DebuggerOptionsObservables);
  }
}
